import { useEffect, useMemo, useRef, useState } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { Globe, User, ChevronDown, Search } from 'lucide-react';
import { useGetApplicationsQuery, useGetRolesQuery } from '../../redux/accessSlice';
import { labelStyle } from './kbStyles';
import {
  normalizeValue,
  isEveryone,
  canMapApp,
  togglePublic,
  toggleRole,
  toggleApp,
  appCheckState,
  isRoleImplied,
  summarize,
} from './visibilityState';

const NARROW_VIEWPORT_BREAKPOINT = 600;

/**
 * Shared "Visible to" control.
 *
 *   mode="article"  (WEB-734) roles only; Everyone = no roles selected and the
 *                   application header toggles all of its roles.
 *   mode="category" (WEB-829) Everyone is an independent public switch, the
 *                   application header maps the whole application, and role
 *                   rows are individual "group" grants. Roles under a mapped
 *                   application show checked and disabled. Roles that belong
 *                   to no application can only be granted individually.
 *
 * `value` is { isPublic, appIds, roleIds } (any subset; it is normalised) and
 * `onChange` receives the complete next value. `disabled` locks the trigger.
 */
const VisibilityPicker = ({
  mode = 'category',
  value: rawValue,
  onChange,
  label = 'Who can view this?',
  idPrefix = 'visibility-picker',
  disabled = false,
}) => {
  const value = useMemo(() => normalizeValue(rawValue, mode), [rawValue, mode]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef(null);
  const [isNarrowViewport, setIsNarrowViewport] = useState(
    typeof window !== 'undefined' ? window.innerWidth < NARROW_VIEWPORT_BREAKPOINT : false,
  );

  const { data: roles = [], isLoading: rolesLoading, isError: rolesErrored } = useGetRolesQuery();
  const { data: applications = [], isLoading: appsLoading, isError: appsErrored } = useGetApplicationsQuery();
  const loading = rolesLoading || appsLoading;
  const errored = rolesErrored || appsErrored;

  useEffect(() => {
    const handleResize = () => setIsNarrowViewport(window.innerWidth < NARROW_VIEWPORT_BREAKPOINT);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // One group per application with its roles beneath, alphabetical by app.
  // Category mode lists every application (an app with no roles can still be
  // mapped); article mode only shows apps that have roles to pick, matching
  // the original picker. Roles whose app is unknown fall into one group whose
  // header cannot be mapped.
  const groups = useMemo(() => {
    const byApp = new Map(applications.map((a) => [a.app_id, { app: a, roles: [] }]));
    roles.forEach((r) => {
      if (!byApp.has(r.app_id)) byApp.set(r.app_id, { app: { app_id: r.app_id, app_name: 'Unknown App' }, roles: [] });
      byApp.get(r.app_id).roles.push(r);
    });
    return [...byApp.values()]
      .filter((g) => mode === 'category' || g.roles.length > 0)
      .sort((a, b) => a.app.app_name.localeCompare(b.app.app_name));
  }, [applications, roles, mode]);

  const q = search.trim().toLowerCase();
  const visibleGroups = useMemo(
    () =>
      groups.flatMap((g) => {
        if (q === '') return [{ ...g, visibleRoles: g.roles }];
        const appMatches = mode === 'category' && g.app.app_name.toLowerCase().includes(q);
        const visibleRoles = appMatches ? g.roles : g.roles.filter((r) => r.role_name.toLowerCase().includes(q));
        return appMatches || visibleRoles.length > 0 ? [{ ...g, visibleRoles }] : [];
      }),
    [groups, q, mode],
  );

  const everyone = isEveryone(value, mode);
  const emit = (next) => onChange(normalizeValue(next, mode));
  const triggerLabel = loading && !everyone ? 'Loading…' : summarize(value, applications, roles, mode);

  return (
    <>
      <Form.Label className="text-muted text-uppercase d-block" style={labelStyle}>
        {label}
      </Form.Label>

      <div ref={wrapperRef} style={{ position: 'relative', display: 'inline-block' }}>
        <button
          type="button"
          onClick={() => setOpen((s) => !s)}
          disabled={disabled}
          aria-expanded={open}
          aria-haspopup="true"
          className="d-inline-flex align-items-center gap-2 px-3 py-1"
          style={{
            cursor: disabled ? 'not-allowed' : 'pointer',
            border: '1px solid #ced4da',
            borderRadius: '16px',
            fontSize: '13px',
            background: everyone ? '#e9f7ef' : '#f8f9fa',
            color: everyone ? '#1e7e34' : '#333',
            opacity: disabled ? 0.6 : 1,
            width: '260px',
          }}
        >
          {everyone ? <Globe size={14} /> : <User size={14} />}
          <strong className="flex-shrink-0">Visible to:</strong>
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
              minWidth: 0,
              textAlign: 'left',
            }}
          >
            {triggerLabel}
          </span>
          <ChevronDown size={12} color="#999" className="flex-shrink-0" />
        </button>

        {open && !disabled && (
          <div
            style={{
              position: 'absolute',
              ...(isNarrowViewport
                ? { top: 'calc(100% + 8px)', left: 0 }
                : { bottom: 0, left: 'calc(100% + 8px)' }),
              zIndex: 1071,
              width: '340px',
              maxWidth: 'calc(100vw - 32px)',
              background: 'white',
              border: '1px solid #dee2e6',
              borderRadius: '8px',
              boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
            }}
          >
            <div className="p-2 border-bottom position-relative">
              <Search
                size={14}
                color="#999"
                style={{
                  position: 'absolute',
                  left: '20px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                }}
              />
              <Form.Control
                size="sm"
                autoFocus
                placeholder={mode === 'category' ? 'Search applications or roles…' : 'Search roles…'}
                aria-label={mode === 'category' ? 'Search applications or roles' : 'Search roles'}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ paddingLeft: '28px' }}
              />
            </div>

            <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
              {loading && (
                <div className="d-flex align-items-center gap-2 p-3 text-muted" style={{ fontSize: '13px' }}>
                  <Spinner size="sm" animation="border" /> Loading roles…
                </div>
              )}

              {!loading && errored && (
                <div className="p-3 text-danger" style={{ fontSize: '13px' }}>
                  Couldn't load roles. Please close and reopen this dialog to try again.
                </div>
              )}

              {!loading && !errored && (
                <>
                  {/* Form.Check's built-in label pairing makes the whole row clickable and keyboard-operable. */}
                  <div
                    className="px-3 py-2"
                    style={{ borderBottom: '1px solid #eee', background: everyone ? '#f8f9fa' : 'white' }}
                  >
                    <Form.Check
                      type="checkbox"
                      id={`${idPrefix}-everyone`}
                      checked={everyone}
                      onChange={() => emit(togglePublic(value, mode))}
                      label={
                        <span className="d-flex align-items-center gap-1" style={{ fontWeight: 700, fontSize: '13px' }}>
                          <Globe size={14} /> Everyone
                          {mode === 'category' && (
                            <span className="text-muted fw-normal ms-1">(including signed-out visitors)</span>
                          )}
                        </span>
                      }
                    />
                  </div>

                  {mode === 'category' && everyone && (
                    <div className="px-3 py-2 text-muted" style={{ fontSize: '12px', borderBottom: '1px solid #eee' }}>
                      Everyone can see this category. Application and role selections below apply once Everyone is unchecked.
                    </div>
                  )}

                  {q !== '' && visibleGroups.length === 0 && (
                    <div className="text-muted p-3" style={{ fontSize: '13px' }}>
                      No matches for '{search}'
                    </div>
                  )}

                  {visibleGroups.map(({ app, roles: rolesOfApp, visibleRoles }) => {
                    const state = appCheckState(value, app, rolesOfApp, mode);
                    const mappable = mode !== 'category' || canMapApp(app);
                    return (
                      <div key={app.app_id ?? 'unknown'}>
                        <div className="px-3 py-2" style={{ borderBottom: '1px solid #eee', background: '#fafafa' }}>
                          <Form.Check
                            type="checkbox"
                            id={`${idPrefix}-app-${app.app_id ?? 'unknown'}`}
                            checked={state === 'all'}
                            ref={(el) => {
                              if (el) el.indeterminate = state === 'some';
                            }}
                            onChange={() => emit(toggleApp(value, app, rolesOfApp, mode))}
                            disabled={!mappable}
                            title={mappable ? undefined : 'These roles belong to no application; grant them individually'}
                            label={<span style={{ fontWeight: 700, fontSize: '13px' }}>{app.app_name}</span>}
                          />
                        </div>
                        {visibleRoles.map((role) => {
                          const implied = isRoleImplied(value, role, mode);
                          return (
                            <div key={role.role_id} className="ps-4 pe-3 py-1">
                              <Form.Check
                                type="checkbox"
                                id={`${idPrefix}-role-${role.role_id}`}
                                checked={implied || value.roleIds.includes(role.role_id)}
                                disabled={implied}
                                onChange={() => emit(toggleRole(value, role, mode))}
                                label={
                                  <span
                                    title={implied ? `${role.role_name} (included with ${app.app_name})` : role.role_name}
                                    style={{
                                      fontSize: '13px',
                                      display: 'inline-block',
                                      maxWidth: '230px',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      verticalAlign: 'middle',
                                    }}
                                  >
                                    {role.role_name}
                                  </span>
                                }
                              />
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default VisibilityPicker;
